import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Footer from "../home/footer";
import { gsap } from "gsap";

import api, { formatFileUrl } from "../../utils/api";
import { STATIC_EVENTS } from "../gallary/staticEventsData";

const ExpertCards = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [eventData, setEventData] = useState(null);
  const [loading, setLoading] = useState(true);
  const containerRef = useRef(null);

  useEffect(() => {
    const fetchEvent = async () => {
      setLoading(true);
      if (!id) {
        setEventData(STATIC_EVENTS[0]);
        setLoading(false);
        return;
      }

      const cleanId = String(id).replace("static-", "");
      const foundStatic = STATIC_EVENTS.find(
        (e) => e._id === id || e.id === cleanId || e._id === `static-${cleanId}`
      );

      if (foundStatic) {
        setEventData(foundStatic);
        setLoading(false);
        return;
      }

      try {
        const res = await api.get(`/gallery/${id}`);
        setEventData(res.data);
      } catch (err) {
        console.error("Failed to fetch gallery details", err);
        setEventData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchEvent();
  }, [id]);

  useEffect(() => {
    if (loading || !eventData) return;
    window.scrollTo(0, 0);

    gsap.fromTo(
      containerRef.current,
      { opacity: 0, y: 30 },
      { opacity: 1, y: 0, duration: 0.8, ease: "power3.out" }
    );
  }, [loading, eventData]);

  const [selectedImageIndex, setSelectedImageIndex] = useState(null);

  const openImage = (index) => {
    setSelectedImageIndex(index);
  };

  const closeModal = () => {
    setSelectedImageIndex(null);
  };

  const navigateImages = (direction) => {
    if (selectedImageIndex !== null && eventData?.gridImages) {
      const newIndex =
        (selectedImageIndex + direction + eventData.gridImages.length) % eventData.gridImages.length;
      setSelectedImageIndex(newIndex);
    }
  };

  const handleKeyDown = (event) => {
    if (selectedImageIndex !== null) {
      if (event.key === "ArrowRight") {
        navigateImages(1);
      } else if (event.key === "ArrowLeft") {
        navigateImages(-1);
      } else if (event.key === "Escape") {
        closeModal();
      }
    }
  };

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [selectedImageIndex]);

  const handleChange = () => {
    navigate("/gallery");
  };

  return (
    <>
      {loading ? (
        <div className="w-full h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black"></div>
        </div>
      ) : !eventData ? (
        <div className="w-full h-screen flex flex-col items-center justify-center">
          <h2 className="text-2xl font-bold mb-4">Event not found</h2>
          <button onClick={handleChange} className="px-6 py-2 bg-black text-white rounded-lg">Go back to Gallery</button>
        </div>
      ) : (
      <div
        ref={containerRef}
        className="w-full max-w-7xl mx-auto px-4 relative bg-white md:mt-10 md:pt-10 mt-14 flex flex-col min-h-screen"
      >
        <button
          onClick={handleChange}
          className="sm:flex hidden font-medium absolute top-7 cursor-pointer z-[45] left-2 items-center gap-2 px-3 py-2 bg-gradient-to-r from-[#263238] to-[#37474F] text-white rounded-full shadow-lg hover:from-[#37474F] hover:to-[#263238] transition-all duration-300 transform "
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="icon icon-tabler icon-tabler-arrow-left"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            strokeWidth="1.5"
            stroke="currentColor"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path stroke="none" d="M0 0h24v24H0z" fill="none" />
            <path d="M5 12l14 0" />
            <path d="M5 12l6 -6" />
            <path d="M5 12l6 6" />
          </svg>
          <span className="text-sm">Back</span>
        </button>

        <div className="absolute w-52 h-52 bg-orange-400 z-30 rounded-full blur-[130px] opacity-30 pointer-events-none"></div>
        <div className="absolute w-52 h-52 right-[50%] top-[40%] z-30 bg-orange-400 rounded-full blur-[120px] opacity-30 pointer-events-none"></div>
        <div className="absolute w-52 h-52 right-0 top-10 z-30 bg-orange-400 rounded-full blur-[130px] opacity-30 pointer-events-none md:block hidden"></div>
        <div className="absolute w-52 h-52 left-0 bottom-0 z-30 bg-orange-400 rounded-full blur-[130px] opacity-30 pointer-events-none"></div>

        <div>
          <h1 className="text-center w-full font-semibold md:text-3xl text-xl py-5 pt-8 md:pt-5 relative z-40 font-AndoraModernSerif">
            {eventData.title}
          </h1>
          <p className="text-start md:text-center text-base text-black mb-12 max-w-3xl mx-auto relative z-40">
            {eventData.headerDesc || eventData.description}
          </p>
        </div>

        {eventData.gridImages && eventData.gridImages.length > 0 && (
          <>
            <div className="hidden md:block relative mb-12">
              <div className="grid grid-cols-4 md:px-20 gap-6 md:pt-10">
                {eventData.gridImages.map((imgUrl, index) => {
                  const pos = index % 2 === 0 ? "top" : "bottom";
                  return (
                    <div
                      key={index}
                      className={`transform z-40 relative ${pos === "top" ? "-translate-y-8" : "translate-y-8"}`}
                    >
                      <div
                        className="relative z-50 overflow-hidden rounded-lg shadow-md group cursor-pointer"
                        onClick={() => openImage(index)}
                      >
                        <img
                          src={formatFileUrl(imgUrl)}
                          alt={`Gallery ${index + 1}`}
                          className="w-full h-64 z-50 relative object-cover transform transition-transform duration-300 group-hover:scale-110"
                        />
                        <div className="absolute z-50 inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                          <p className="text-white font-semibold">View Image</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="md:hidden grid grid-cols-1 sm:grid-cols-2 gap-6 px-4 mb-12">
              {eventData.gridImages.map((imgUrl, index) => (
                <div
                  key={index}
                  className="bg-white rounded-lg overflow-hidden shadow-lg cursor-pointer"
                  onClick={() => openImage(index)}
                >
                  <img
                    src={formatFileUrl(imgUrl)}
                    alt={`Event ${index + 1}`}
                    className="w-full h-64 object-cover relative z-40"
                  />
                </div>
              ))}
            </div>
          </>
        )}

        {(eventData.imageUrl || eventData.bottomTitle || eventData.bottomDesc1) && (
          <section className="text-center md:mb-16 mb-8 flex flex-wrap items-center gap-5 md:gap-0 z-40 md:mt-16">
            <h1 className="text-center w-full font-semibold md:text-3xl text-xl md:py-8 pt-6 font-AndoraModernSerif z-40">
              {eventData.bottomTitle || "Event Highlights"}
            </h1>
            {eventData.imageUrl && (
              <div className="md:w-1/2 w-full z-40 mt-5 md:mt-0 px-4">
                <img
                  src={formatFileUrl(eventData.imageUrl)}
                  alt={eventData.title}
                  className="rounded-lg w-full max-w-md mx-auto shadow-xl object-cover"
                />
              </div>
            )}
            <div className={`${eventData.imageUrl ? "md:w-1/2" : "w-full text-center"} w-full text-black px-4 text-start`}>
              {eventData.bottomDesc1 && (
                <p className="z-40 md:text-lg text-base text-gray-700 leading-relaxed mb-4">
                  {eventData.bottomDesc1}
                </p>
              )}
              {eventData.bottomDesc2 && (
                <p className="z-40 text-base text-gray-600 leading-relaxed">
                  {eventData.bottomDesc2}
                </p>
              )}
            </div>
          </section>
        )}
      </div>
      )}

      {selectedImageIndex !== null && eventData?.gridImages && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4">
          <button
            className="absolute top-6 right-6 text-white text-3xl font-bold bg-black/50 hover:bg-black/80 w-12 h-12 rounded-full flex items-center justify-center transition-all z-50"
            onClick={closeModal}
            aria-label="Close modal"
          >
            &times;
          </button>
          <div className="relative max-w-5xl max-h-[90vh] flex items-center justify-center">
            {eventData.gridImages.length > 1 && (
              <button
                className="absolute left-2 md:-left-12 text-white bg-black/60 hover:bg-black/90 px-4 py-3 rounded-full focus:outline-none transition-all z-50 text-xl"
                onClick={() => navigateImages(-1)}
              >
                &#8592;
              </button>
            )}
            <img
              src={formatFileUrl(eventData.gridImages[selectedImageIndex])}
              alt="Selected event memory"
              className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
            />
            {eventData.gridImages.length > 1 && (
              <button
                className="absolute right-2 md:-right-12 text-white bg-black/60 hover:bg-black/90 px-4 py-3 rounded-full focus:outline-none transition-all z-50 text-xl"
                onClick={() => navigateImages(1)}
              >
                &#8594;
              </button>
            )}
          </div>
        </div>
      )}

      <Footer />
    </>
  );
};

export default ExpertCards;

